import { TEAM_MEMBER_TYPES, TTeamMemberType } from '../../../Roles/RoleModal/models/RoleAssignmentModel';
import { markTerminatedUsers } from './utils';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace NTeamManager {
  export interface User {
    userId: string;
    userName: string;
    isActive?: boolean;
    teamMemberType: TTeamMemberType;
  }

  export interface UsersByRole {
    [TEAM_MEMBER_TYPES.Director]: User[];
    [TEAM_MEMBER_TYPES.Lieutenant]: User[];
    [TEAM_MEMBER_TYPES.Member]: User[];
  }

  export interface TeamRequest {
    name: string;
    teamTypeId: string | null;
    periodId: string | null;
    copiedFromTeamId?: string;
    users: User[];
    roleUsers: UsersByRole;
    index: number;
  }

  export interface Team extends TeamRequest {
    id: string;
    isNew?: boolean;
  }

  export interface UpdateManagerRequest {
    userId: string;
    teamTypeId: string;
    periodId: string;
    isDeleted?: boolean;
  }

  export interface UpdateTeamRequest {
    teamId?: string;
    updateTeamRequest: TeamRequest;
  }

  export interface Request {
    updateManagerRequests: UpdateManagerRequest[];
    updateTeamRequests: UpdateTeamRequest[];
  }

  export interface Response {
    managers: User[];
    unassignedUsers: User[];
    teams: Team[];
  }
}

export interface IUnassignedUsersResponse {
  unassignedUsers: NTeamManager.User[];
}

const generateRoleUsers = (users: NTeamManager.User[] = []) => {
  const result: NTeamManager.UsersByRole = {
    [TEAM_MEMBER_TYPES.Director]: [],
    [TEAM_MEMBER_TYPES.Lieutenant]: [],
    [TEAM_MEMBER_TYPES.Member]: [],
  };

  users.forEach(({ isActive: _isActive, ...user }) => {
    result[user.teamMemberType].push(user);
  });

  return result;
};

const filterTeamUsers = (response: NTeamManager.Response) => {
  const teamUsersSet = new Set();

  response?.teams?.forEach(
    (team) =>
      team?.users?.forEach((user) => {
        teamUsersSet.add(user.userId);
      }),
  );

  return response.unassignedUsers?.filter((user) => !teamUsersSet.has(user.userId));
};

export class TeamModel implements Partial<NTeamManager.Team> {
  id = '';
  name = 'New Team';
  teamTypeId = '';
  periodId = '';
  copiedFromTeamId?: string = '';
  users: NTeamManager.User[] = [];
  index = 0;
  roleUsers: NTeamManager.UsersByRole = {
    [TEAM_MEMBER_TYPES.Director]: [],
    [TEAM_MEMBER_TYPES.Lieutenant]: [],
    [TEAM_MEMBER_TYPES.Member]: [],
  };

  constructor(data?: Partial<NTeamManager.Team>) {
    if (data) {
      this.id = data.id || '';
      this.name = data?.name || '';
      this.teamTypeId = data.teamTypeId || '';
      this.periodId = data.periodId || '';
      this.copiedFromTeamId = data.copiedFromTeamId || '';
      this.users = data.users || [];
      this.roleUsers = generateRoleUsers(data.users);
      this.index = data.index || 0;
    }
  }
}

export class TeamManagerModel implements NTeamManager.Response {
  managers: NTeamManager.User[] = [];
  unassignedUsers: NTeamManager.User[] = [];
  teams: NTeamManager.Team[] = [];

  constructor(data?: NTeamManager.Response) {
    const mapUser = ({ isActive: _isActive, ...user }: NTeamManager.User) => user;

    if (data) {
      this.managers = data.managers?.map(mapUser) || [];
      this.unassignedUsers = markTerminatedUsers(filterTeamUsers(data) || []);
      this.teams = data?.teams?.map((team, index) => {
        const newTeam = new TeamModel(team);
        newTeam.index = index;
        return newTeam;
      }) || [];
    }
  }
}
